<template>
  <div class="gallery-content section-padding">
    <div class="container">
      <div class="row">
        <div class="col-sm-12">
          <div ref="gallerySwiperRoot" class="gallery-swiper">
            <div class="swiper-container">
              <div class="swiper-wrapper">
                <div
                  class="swiper-slide"
                  :key="image._id || image.id || index"
                  v-for="(image, index) in displayableImages"
                >
                  <article class="gallery-item">
                  <div class="gallery-item__media">
                    <a
                      href="javascript:;"
                      :data-fancybox="identifier"
                      class="gallery-item__link"
                      @click.prevent="openLightbox(image)"
                    >
                      <img
                        v-if="getThumbSrc(image)"
                        :src="getThumbSrc(image)"
                        :alt="getImageLabel(image)"
                        class="gallery-item__img"
                        loading="lazy"
                        decoding="async"
                        @error="handleImageError($event, image)"
                      />
                      <div
                        v-else-if="isThumbLoading(image)"
                        class="gallery-item__placeholder gallery-item__placeholder--loading"
                        aria-busy="true"
                        aria-label="Bild wird geladen"
                      >
                        <span class="gallery-item__spinner" aria-hidden="true"></span>
                      </div>
                      <div
                        v-else
                        class="gallery-item__placeholder gallery-item__placeholder--empty"
                      >
                        <p>Bild nicht verfügbar</p>
                      </div>
                    </a>
                  </div>
                  <p v-if="getImageLabel(image)" class="gallery-item__caption">
                    {{ getImageLabel(image) }}
                  </p>
                  </article>
                </div>
              </div>
            </div>
            <div v-if="displayableImages.length > 1" class="text-center gallery-swiper__pagination">
              <div class="swiper-pagination top40"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import {
  extractImageId,
  resolveImageField,
  resolveLegacyImageUrl,
  loadProcessImageUrl
} from "../../lib/helper.js";
import {
  SWIPER_MOBILE_BREAKPOINTS,
} from "../../lib/swiperInit.js";
import { useJQuerySwiper, useJQuerySwiperWatch } from "../../composables/useJQuerySwiper.js";

const isProcessImageUrl = (url) =>
  typeof url === "string" &&
  url.includes("/api/v2/images/") &&
  url.includes("/process?");

export default {
  setup(props) {
    const gallerySwiperRoot = ref(null);
    const retriedImages = ref(new Set());
    const thumbSrcById = ref({});
    const loadingThumbKeys = ref(new Set());
    const failedThumbKeys = ref(new Set());

    const getImageKey = (image) => image._id || image.id || extractImageId(image);

    const getMyPortalImageId = (image) => extractImageId(image);

    const getLegacyImageUrl = (image) => resolveLegacyImageUrl(image);

    const getGalleryThumb = (image) => {
      const imageId = getMyPortalImageId(image);
      if (imageId) {
        return resolveImageField(imageId, null, "thumb");
      }

      return getLegacyImageUrl(image);
    };

    const getGalleryFull = (image) => {
      const imageId = getMyPortalImageId(image);
      if (imageId) {
        return resolveImageField(imageId, null, "gallery");
      }

      return getLegacyImageUrl(image);
    };

    const getThumbSrc = (image) => {
      const key = getImageKey(image);
      if (key && thumbSrcById.value[key]) {
        return thumbSrcById.value[key];
      }

      const candidate = getGalleryThumb(image);
      if (candidate && !isProcessImageUrl(candidate)) {
        return candidate;
      }

      return "";
    };

    const isThumbLoading = (image) => {
      if (getThumbSrc(image)) return false;

      const key = getImageKey(image);
      const candidate = getGalleryThumb(image);
      if (!candidate || !isProcessImageUrl(candidate)) return false;

      if (key && failedThumbKeys.value.has(key)) return false;

      return true;
    };

    const queueThumbLoads = (images) => {
      (images || []).forEach((image) => {
        const key = getImageKey(image);
        const candidate = getGalleryThumb(image);
        if (!key || !candidate) return;

        if (!isProcessImageUrl(candidate)) {
          thumbSrcById.value[key] = candidate;
          return;
        }

        failedThumbKeys.value.delete(key);

        if (loadingThumbKeys.value.has(key) || thumbSrcById.value[key]) return;

        loadingThumbKeys.value.add(key);
        loadProcessImageUrl(candidate).then((loaded) => {
          loadingThumbKeys.value.delete(key);
          if (loaded) {
            thumbSrcById.value[key] = loaded;
          } else {
            failedThumbKeys.value.add(key);
          }
        });
      });
    };

    const getImageLabel = (image) =>
      image?.title || image?.description || image?.label || "";

    const hasDisplayableImage = (image) =>
      Boolean(getThumbSrc(image) || getGalleryThumb(image) || getLegacyImageUrl(image));

    const displayableImages = computed(() =>
      (props.images || []).filter((image) => hasDisplayableImage(image))
    );

    const getGallerySwiperOptions = () => ({
      slidesPerView: 4,
      spaceBetween: 20,
      breakpoints: SWIPER_MOBILE_BREAKPOINTS,
    });

    const { scheduleInit } = useJQuerySwiper({
      rootRef: gallerySwiperRoot,
      canInit: () => displayableImages.value.length > 0,
      buildOptions: getGallerySwiperOptions,
    });

    useJQuerySwiperWatch(
      () => [props.images, thumbSrcById.value, failedThumbKeys.value],
      () => {
        queueThumbLoads(props.images);
        scheduleInit();
      },
      { deep: true, immediate: true }
    );

    const handleImageError = (event, image) => {
      const img = event?.target;
      if (!img) return;

      const key = getMyPortalImageId(image) || img.src;
      if (retriedImages.value.has(key)) {
        img.style.display = "none";
        return;
      }

      retriedImages.value.add(key);

      const retryCandidate =
        getGalleryFull(image) || getGalleryThumb(image) || getLegacyImageUrl(image);

      if (!retryCandidate) {
        img.style.display = "none";
        return;
      }

      loadProcessImageUrl(retryCandidate, { retries: 3, delayMs: 600 }).then(
        (retryUrl) => {
          if (retryUrl && retryUrl !== img.src) {
            img.src = retryUrl;
            const imageKey = getImageKey(image);
            if (imageKey) {
              thumbSrcById.value[imageKey] = retryUrl;
              failedThumbKeys.value.delete(imageKey);
            }
            return;
          }

          const imageKey = getImageKey(image);
          if (imageKey) failedThumbKeys.value.add(imageKey);
          img.style.display = "none";
        }
      );
    };

    const openLightbox = async (image) => {
      const $ = window.$;
      if (!$?.fancybox || !props.images?.length) return;

      const items = [];
      let openIndex = 0;
      const selectedKey = getImageKey(image);

      for (let itemIndex = 0; itemIndex < props.images.length; itemIndex += 1) {
        const item = props.images[itemIndex];
        const rawSrc = getGalleryFull(item) || getGalleryThumb(item);
        if (!rawSrc) continue;

        const src = isProcessImageUrl(rawSrc)
          ? await loadProcessImageUrl(rawSrc)
          : rawSrc;
        if (!src) continue;

        if (getImageKey(item) === selectedKey) {
          openIndex = items.length;
        }

        items.push({
          src,
          type: "image",
          caption: item.title || item.description || item.label || ""
        });
      }

      if (!items.length) return;

      $.fancybox.open(items, {
        index: openIndex,
        loop: true,
        infobar: true,
        buttons: ["slideShow", "fullScreen", "thumbs", "close"]
      });
    };

    return {
      gallerySwiperRoot,
      displayableImages,
      getGalleryThumb,
      getThumbSrc,
      getImageLabel,
      isThumbLoading,
      hasDisplayableImage,
      handleImageError,
      openLightbox
    };
  },
  props: {
    images: Array,
    identifier: String
  }
};
</script>

<style scoped lang="scss">
.section-padding {
  padding-bottom: 60px;
}

.gallery-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(81, 90, 95, 0.12);
  box-shadow: 0 6px 28px rgba(15, 23, 42, 0.08);
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0 10px 36px rgba(15, 23, 42, 0.12);
  }
}

.gallery-item__media {
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #f0f2f4;
  flex-shrink: 0;
  line-height: 0;
}

.gallery-item__link {
  position: absolute;
  inset: 0;
  display: block;
  margin: 0;
  padding: 0;
}

.gallery-item__img {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.45s ease;
}

.gallery-item:hover .gallery-item__img {
  transform: scale(1.03);
}

.gallery-item__placeholder {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  background-color: #f0f2f4;
  display: flex;
  align-items: center;
  justify-content: center;

  &--loading {
    background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
    background-size: 200% 100%;
    animation: gallery-shimmer 1.4s linear infinite;
  }

  &--empty {
    background-color: #e4e7eb;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: #888;
  }
}

.gallery-item__spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(66, 217, 228, 0.25);
  border-top-color: #42d9e4;
  border-radius: 50%;
  animation: gallery-spin 0.75s linear infinite;
}

.gallery-item__caption {
  margin: 0;
  padding: 0.75rem 0.85rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.45;
  color: #515a5f;
  text-align: center;
}

@keyframes gallery-shimmer {
  to {
    background-position-x: -200%;
  }
}

@keyframes gallery-spin {
  to {
    transform: rotate(360deg);
  }
}

.gallery-swiper :deep(.swiper-container) {
  overflow: hidden;
  touch-action: pan-y;
}

.gallery-swiper :deep(.swiper-slide) {
  height: auto;
  display: flex;
  align-items: stretch;
  padding: 10px 6px 24px;
  box-sizing: border-box;

  > .gallery-item {
    flex: 1 1 auto;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .section-padding {
    padding-bottom: 1.5rem;
  }
}
</style>
